import { MdClose } from 'react-icons/md';
import useTheme from '../../context/ThemeContext';

// Shared shell for the per-role "Edit Details" modals (Students / Trainers / Admins). Matches the
// Add Member modal shell: fixed overlay, sticky header, scrollable body, sticky footer that becomes
// a comfortable mobile sheet. Semantic tokens only — renders correctly in light and dark.
export default function EditDetailsModal({ title, subtitle, onClose, onSubmit, saving, hasLogin = false, children }) {
  const { currentTheme } = useTheme();
  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-card rounded-3xl shadow-2xl border border-divider w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header (sticky) */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-divider flex-shrink-0">
          <div>
            <h3 className="font-bold text-secondary text-lg">{title}</h3>
            {subtitle && <p className="text-xs text-muted mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 text-muted hover:text-secondary hover:bg-nav-hover rounded-lg transition-colors">
            <MdClose className="text-xl" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col min-h-0 flex-1">
          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            {children}
          </div>

          {/* Actions (sticky footer) */}
          <div className="flex-shrink-0 px-6 py-3 border-t border-divider">
            {hasLogin && (
              <p className="text-[11px] text-subtle mb-2 text-center">Login email can&apos;t be changed here yet.</p>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl border border-divider text-sm font-medium text-secondary hover:bg-nav-hover transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-colors ${primaryBtn}`}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// Shared field primitives (same look as the Add Member modal).
export const inputCls = (err) =>
  `w-full px-3 py-2.5 rounded-xl text-sm bg-nav-hover/40 border text-secondary placeholder:text-subtle
   focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors
   ${err ? 'border-red-500/50' : 'border-divider focus:border-primary/40'}`;

export const labelCls = 'block text-xs font-medium text-muted mb-1';

export function Field({ label, required, error, children }) {
  return (
    <div>
      {label && (
        <label className={labelCls}>
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}
